/** 编辑手工支出时管理付款凭证：附件单独保存，失败保留原列表，不丢弃尚未保存的账目字段。 */
import React, { useEffect, useState } from "react";
import { Loader2, Trash2 } from "lucide-react";
import { Button } from "../ui/button";
import { toast } from "../ui/sonner";
import { OutflowAttachmentPreview, OutflowAttachmentUpload } from "./OutflowAttachments";

export function OutflowAttachmentManager({ api, pid, row, zh = true, disabled = false, onChanged, onBusyChange }) {
  const [attachments, setAttachments] = useState(row.attachments || []);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [refreshError, setRefreshError] = useState(false);
  const locked = disabled || busy || uploadBusy;
  useEffect(() => { onBusyChange?.(busy || uploadBusy); }, [busy, uploadBusy, onBusyChange]);
  useEffect(() => () => onBusyChange?.(false), [onBusyChange]);
  const refresh = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/finance/project/${pid}/outflows`);
      const updated = data.rows?.find(item => item.id === row.id && item.kind === row.kind);
      if (!updated) throw new Error(zh ? "该记录已变更，请关闭后刷新页面" : "Payment changed. Close and refresh the page.");
      setAttachments(updated.attachments || []);
      setRefreshError(false);
      await onChanged?.();
    } catch (e) {
      setRefreshError(true);
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!confirm?.canDelete || locked) return;
    setBusy(true);
    try {
      const { data } = await api.delete(`/finance/project/${pid}/outflows/${row.kind}/${row.id}/attachments/${encodeURIComponent(confirm.id)}`);
      setAttachments(data.attachments || []);
      setConfirm(null);
      toast.success(zh ? "附件已移除，可重新上传" : "Attachment removed. You can upload a replacement.");
      await onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };
  return <section className="min-w-0 rounded-xl border border-slate-200 p-3 space-y-3" data-testid="outflow-attachment-manager">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <h3 className="text-sm font-semibold text-slate-800">{zh ? "支付凭证 / 附件" : "Payment proof / attachments"}</h3>
      <OutflowAttachmentUpload api={api} pid={pid} row={row} zh={zh} canUpload={!disabled && !busy} onChanged={refresh} onBusyChange={setUploadBusy} />
    </div>
    <p className="text-xs text-slate-500">{zh ? "附件操作即时保存；账目修改请点击下方保存。" : "Attachment changes save immediately. Use Save below for payment details."}</p>
    {attachments.length === 0 ? <p className="text-sm text-slate-500">{zh ? "暂无附件" : "No attachments"}</p> : <ul className="space-y-2">
      {attachments.map(file => <li key={file.id} className="flex min-w-0 items-center gap-2 rounded-lg bg-slate-50 p-2" data-testid={`outflow-managed-attachment-${file.id}`}>
        <OutflowAttachmentPreview api={api} row={{ ...row, attachments: [file] }} zh={zh} />
        <div className="min-w-0 flex-1"><p className="break-all text-xs text-slate-700">{file.filename}</p>
          {!file.canDelete && <p className="text-xs text-slate-500">{zh ? "系统凭证，请在来源流程维护" : "System proof: manage in its source workflow"}</p>}
        </div>
        {file.canDelete && <Button type="button" variant="ghost" size="icon" disabled={locked} aria-label={`${zh ? "删除附件" : "Remove attachment"}: ${file.filename}`} onClick={() => setConfirm(file)}><Trash2 className="h-4 w-4 text-red-600" /></Button>}
      </li>)}
    </ul>}
    {refreshError && <div role="alert" className="flex items-center justify-between gap-2 text-xs text-red-700"><span>{zh ? "附件列表更新失败，请重试" : "Could not refresh attachments"}</span><Button type="button" size="sm" variant="outline" disabled={locked} onClick={refresh}>{zh ? "重试" : "Retry"}</Button></div>}
    {confirm && <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 space-y-2" data-testid="outflow-attachment-delete-confirmation">
      <p className="break-words text-sm text-slate-800">{zh ? `移除此支出的附件「${confirm.filename}」？` : `Remove “${confirm.filename}” from this payment?`}</p>
      <p className="text-xs text-slate-600">{zh ? "保留原始文件和操作记录，不改变付款金额及状态。" : "Original file and audit history are retained. Payment amount and status stay unchanged."}</p>
      <div className="flex justify-end gap-2"><Button type="button" size="sm" variant="outline" disabled={locked} onClick={() => setConfirm(null)}>{zh ? "取消" : "Cancel"}</Button><Button type="button" size="sm" variant="destructive" disabled={locked} onClick={remove} data-testid="outflow-attachment-delete-confirm">{busy && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}{zh ? "确认移除" : "Confirm removal"}</Button></div>
    </div>}
  </section>;
}
